import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { EChartsCoreOption } from 'echarts/core';
import { Observable } from 'rxjs';
import { Client } from 'src/app/entities/client/client';
import { Project } from 'src/app/entities/project/project';
import { Tag } from 'src/app/entities/tag/tag';
import { TimeTrack } from 'src/app/entities/time-track/time-track';
import { User } from 'src/app/entities/user/user';
import { FiltersService, ReportsFormValues } from 'src/app/services/filters-service/filters.service';
import { RemoteDataService } from 'src/app/services/remote-data-service/remote-data.service';
import { StoreService } from 'src/app/services/store-service/store.service';
import { ExportationService } from 'src/app/services/exportation-service/exportation.service';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { UserRepository } from 'src/app/services/remote-data-service/repositories/user-repository/user.repository';
import { DateService } from 'src/app/services/date-service/date.service';
import { ReportsSummaryComponent } from 'src/app/components/molecules/reports-summary/reports-summary.component';
import { ReportsDetailedComponent } from 'src/app/components/molecules/reports-detailed/reports-detailed.component';
import { ReportsWeeklyComponent } from 'src/app/components/molecules/reports-weekly/reports-weekly.component';
import { SelectListComponent } from '../../atoms/select-list/select-list.component';
import log from 'loglevel';
import { BaseComponent } from '../../atoms/base-component/base.component';


export enum ReportsCategories {
  Summary = 'summary',
  Detailed = 'detailed',
  Weekly = 'weekly'
}


@Component({
  selector: 'app-reports-page',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    ReportsSummaryComponent,
    ReportsDetailedComponent,
    ReportsWeeklyComponent,
    SelectListComponent
  ],
  templateUrl: './reports-page.component.html',
  styleUrl: './reports-page.component.scss'
})
export class ReportsPageComponent extends BaseComponent implements OnInit {

  /**
   * Logger for the ReportsPage component.
   */
  protected override logger = log.getLogger('ReportsPage');

  projects: Project[] = [];
  filtersProjects: Project[] = [];
  clients: Client[] = [];
  tags: Tag[] = [];
  users: User[] = [];
  timeTracks: TimeTrack[] = [];

  projects$: Observable<Project[]>;
  clients$: Observable<Client[]>;
  tags$: Observable<Tag[]>;
  users$: Observable<User[]>;
  timeTracks$: Observable<TimeTrack[]>


  public reportsFilters = new FormGroup({
    projects: new FormControl<string[]>([], Validators.required),
    clients: new FormControl<string[]>([], Validators.required),
    tags: new FormControl<string[]>([], Validators.required),
    users: new FormControl<string[] | undefined>(undefined, Validators.required)
  })

  /**
   * Options for the `echart` graph.
   */
  options: EChartsCoreOption = {};

  category: ReportsCategories = ReportsCategories.Summary;

  constructor(
    protected store: StoreService,
    protected filters: FiltersService,
    protected remoteDataService: RemoteDataService,
    protected exportService: ExportationService,
    protected router: Router,
    protected userRep: UserRepository,
    protected dateService: DateService

  ) {
    super();

    this.projects$ = this.store.projects;
    this.clients$ = this.store.clients;
    this.tags$ = this.store.tags;
    this.timeTracks$ = this.store.timeTracks;
    this.users$ = this.store.users;

    this.timeTracks$.subscribe((tracks) => {
      this.timeTracks = tracks
    })

    this.projects$.subscribe((projects) => {
      this.projects = projects;
      this.filtersProjects = projects;

      this.setOptionsFromProjects(projects);
    })

    this.clients$.subscribe((clients) => {
      this.clients = clients;

      this.setOptionsFromClients(clients);
    })

    this.tags$.subscribe((tags) => {
      this.tags = tags;

      this.setOptionsFromTags(tags);
    })

    this.users$.subscribe((users) => {
      this.users = users;

      this.setOptionsFromMembers(users);
    })
  }


  ngOnInit(): void {
    this.filters.setTimeTracksFilters({
      limit: 9999999
    })
    this.filters.setUserFilters({
      limit: 99999
    })
    this.filters.setProjectsFilters({
      limit: 99999
    })
    this.filters.setClientsFilters({
      limit: 99999
    })
    this.filters.setTagsFilters({
      limit: 99999
    })

    // Restore saved form values if they exist
    if (this.filters.hasFormValues('reportsFilters')) {
      this.reportsFilters.patchValue(
        this.filters.getFormValues<ReportsFormValues>('reportsFilters') || {}
      );

      this.setSelectedOption(this.selectListMembers(), null, this.reportsFilters.value.users);
      this.setSelectedOption(this.selectListProjects(), null, this.reportsFilters.value.projects);
      this.setSelectedOption(this.selectListClients(), null, this.reportsFilters.value.clients);
      this.setSelectedOption(this.selectListTags(), null, this.reportsFilters.value.tags);
    }

    this.reportsFilters.valueChanges.subscribe((values) => {
      // Save form values
      this.filters.saveFormValues('reportsFilters', values);
      const projectIds = values.projects || [];
      const clientIds = values.clients || [];

      // Si hay clientes seleccionados, añadir sus proyectos al filtro
      const clientProjects = clientIds.length > 0 ?
        this.filtersProjects
          .filter(project => project.client && clientIds.includes(project.client.id))
          .map(project => project.id)
        : [];

      // Combinar los proyectos seleccionados directamente con los proyectos de los clientes
      const allProjectIds = [...new Set([...projectIds, ...clientProjects])];

      this.filters.setTimeTracksFilters({
        projects: allProjectIds,
        tags: values.tags || undefined,
        authors: values.users || undefined,
      });
    });
  }


  /**
   * Method to handle tab changes in the reports page.
   * It updates the date filter based on the current route.
   */
  tabChange(): void {
    const currentFilters = this.filters.timeTracksFilters().get(this.router.url)

    this.filters.setInitDateFilter({
      startDate: currentFilters?.from || this.dateService.getLocalIsosString(new Date()),
      endDate: currentFilters?.to || this.dateService.getLocalIsosString(new Date())
    })
  }


  /**
   * Method to filter time tracks based on the selected filters.
   */
  filteredTimeTracks(): TimeTrack[] {
    const dateFilters = this.filters.timeTracksFilters().get(this.router.url);
    const currentFilters = this.reportsFilters.value;

    return this.timeTracks.filter(track => {
      // Filter by date range
      if (dateFilters?.from && dateFilters?.to) {
        const startDate = new Date(dateFilters.from);
        const endDate = new Date(dateFilters.to);
        const trackDate = track.from;

        if (!(trackDate >= startDate && trackDate <= endDate)) {
          return false;
        }
      }

      // Filter by projects
      if (currentFilters.projects?.length && track.project) {
        if (!currentFilters.projects.includes(track.project.id)) {
          return false;
        }
      }

      // Filter by clients
      if (currentFilters.clients?.length && track.project?.client) {
        if (!currentFilters.clients.includes(track.project.client.id)) {
          return false;
        }
      }

      // Filter by tags
      if (currentFilters.tags?.length && track.tags.length > 0) {
        if (!track.tags.some(tag => currentFilters.tags?.includes(tag.id))) {
          return false;
        }
      }

      // Filter by users
      if (currentFilters.users?.length && track.author) {
        if (!currentFilters.users.includes(track.author.id)) {
          return false;
        }
      }

      return true;
    });
  }

  //convertToPdf = viewChild<ElementRef<HTMLDivElement>>('contentToConvert');

  public async downloadAsPDF(): Promise<void> {
    const dateFilters = this.filters.timeTracksFilters().get(this.router.url);
    const currentFilters = this.reportsFilters.value;

    const reportData = {
      projects: this.projects,
      timeTracks: this.filteredTimeTracks(),
      users: this.users,
      dateRange: {
        start: dateFilters?.from ? new Date(dateFilters.from) : new Date(),
        end: dateFilters?.to ? new Date(dateFilters.to) : new Date()
      },
      filters: {
        projects: currentFilters.projects || [],
        clients: currentFilters.clients || [],
        tags: currentFilters.tags || [],
        users: currentFilters.users || []
      }
    };

    await this.exportService.exportToPdf(reportData);
  }

  public downloadAsCSV(): void {
    const dateFilters = this.filters.timeTracksFilters().get(this.router.url);
    const currentFilters = this.reportsFilters.value;

    const reportData = {
      projects: this.projects,
      timeTracks: this.filteredTimeTracks(),
      users: this.users,
      dateRange: {
        start: dateFilters?.from ? new Date(dateFilters.from) : new Date(),
        end: dateFilters?.to ? new Date(dateFilters.to) : new Date()
      },
      filters: {
        projects: currentFilters.projects || [],
        clients: currentFilters.clients || [],
        tags: currentFilters.tags || [],
        users: currentFilters.users || []
      }
    };

    this.exportService.exportToCsv(reportData);
  }
}
